import axios from 'axios';
import {
  Site, System, SystemUser, ConfigHistory,
  SiteToken, SiteUser as AuthSiteUser, SiteMachine,
  SyncResponse, PlatformRegion, FeedbackListParams, FeedbackListResponse
} from '../types';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_APP_API_BASE_URL
  ? `${process.env.REACT_APP_APP_API_BASE_URL}/api`
  : 'http://localhost:8001/api';

// Token management
const TOKEN_KEY = 'auth_token';
const REGION_KEY = 'selected_region';

// Try localStorage first, fallback to sessionStorage if blocked
let storage: Storage;
try {
  localStorage.setItem('test', 'test');
  localStorage.removeItem('test');
  storage = localStorage;
} catch {
  storage = sessionStorage;
}

export const tokenManager = {
  getToken: (): string | null => {
    return storage.getItem(TOKEN_KEY);
  },
  setToken: (token: string): void => {
    storage.setItem(TOKEN_KEY, token);
  },
  removeToken: (): void => {
    storage.removeItem(TOKEN_KEY);
  },
};

export const regionManager = {
  getRegion: (): string | null => {
    return storage.getItem(REGION_KEY);
  },
  setRegion: (regionName: string): void => {
    storage.setItem(REGION_KEY, regionName);
  },
  removeRegion: (): void => {
    storage.removeItem(REGION_KEY);
  },
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token and region to requests
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }

    // Only add X-Region header if it exists AND if not calling the regions endpoint
    const regionName = regionManager.getRegion();
    const isRegionsEndpoint = config.url?.includes('/auth/regions');
    if (regionName && !isRegionsEndpoint) {
      config.headers['X-Region'] = regionName;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is invalid or expired, clear it
      tokenManager.removeToken();
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API Service functions
export const apiService = {
  // ============== Regions API ==============
  getRegions: async (): Promise<PlatformRegion[]> => {
    const response = await api.get('/auth/regions/');
    return response.data;
  },

  getMyRegions: async (): Promise<PlatformRegion[]> => {
    const response = await api.get('/auth/my-regions/');
    return response.data;
  },

  grantRegionAccess: async (userId: number, regionId: number, notes?: string): Promise<any> => {
    const response = await api.post('/auth/grant-region-access/', {
      user_id: userId,
      region_id: regionId,
      notes: notes || ''
    });
    return response.data;
  },

  revokeRegionAccess: async (userId: number, regionId: number): Promise<any> => {
    const response = await api.post('/auth/revoke-region-access/', {
      user_id: userId,
      region_id: regionId
    });
    return response.data;
  },

  getAllUsers: async (): Promise<any[]> => {
    const response = await api.get('/auth/users/all/');
    return response.data.users;
  },

  listRegionUsers: async (regionId: number): Promise<any> => {
    const response = await api.get(`/auth/regions/${regionId}/users/`);
    return response.data;
  },

  // ============== Authentication API ==============
  login: async (username: string, password: string): Promise<{ token: string; user: any }> => {
    const response = await api.post('/auth/login/', { username, password });
    // Store the token
    if (response.data.token) {
      tokenManager.setToken(response.data.token);
    }
    return response.data;
  },

  logout: async (): Promise<{ message: string }> => {
    const response = await api.post('/auth/logout/');
    // Remove the token and region
    tokenManager.removeToken();
    regionManager.removeRegion();
    return response.data;
  },

  getMicrosoftLoginUrl: async (): Promise<{ login_url: string }> => {
    const response = await api.post('/auth/msal/login/');
    return response.data;
  },

  checkAuth: async (): Promise<{ authenticated: boolean; user?: any }> => {
    const response = await api.get('/auth/check/');
    return response.data;
  },

  // Sites API
  getSites: async (): Promise<Site[]> => {
    const response = await api.get('/sites/list/');
    return response.data;
  },

  createSite: async (siteData: Partial<Site>): Promise<Site> => {
    const response = await api.post('/sites/', siteData);
    return response.data;
  },

  getSiteByName: async (name: string): Promise<Site> => {
    const response = await api.get(`/sites/${name}/`);
    return response.data;
  },

  getSiteByToken: async (token: string): Promise<Site> => {
    const response = await api.get(`/sites/token/${token}/`);
    return response.data;
  },

  getSiteById: async (siteId: number): Promise<Site> => {
    const response = await api.get(`/sites/${siteId}/`);
    return response.data;
  },

  updateSiteConfig: async (siteId: number, config: Record<string, any>): Promise<Site> => {
    const response = await api.put(`/sites/${siteId}/config/`, { config });
    return response.data;
  },

  updateSiteUIConfig: async (siteId: number, uiConfig: Record<string, any>): Promise<Site> => {
    const response = await api.put(`/sites/${siteId}/ui-config/`, { ui_config: uiConfig });
    return response.data;
  },

  getSiteConfigHistory: async (siteId: number, configType?: 'config' | 'ui_config'): Promise<ConfigHistory[]> => {
    const params = configType ? { config_type: configType } : {};
    const response = await api.get(`/sites/${siteId}/config/history/`, { params });
    return response.data;
  },

  // Systems API
  getSystems: async (): Promise<System[]> => {
    const response = await api.get('/systems/list/');
    return response.data;
  },

  getSystem: async (identifier: string): Promise<System> => {
    const response = await api.get(`/systems/${identifier}/`);
    return response.data;
  },

  getSystemConfig: async (identifier: string): Promise<{ config: Record<string, any> }> => {
    const response = await api.get(`/systems/${identifier}/config/`);
    return response.data;
  },

  registerSystem: async (systemData: Partial<System>): Promise<System> => {
    const response = await api.post('/systems/', systemData);
    return response.data;
  },

  updateSystemConfig: async (identifier: string, config: Record<string, any>): Promise<System> => {
    const response = await api.put(`/systems/${identifier}/config/update/`, { config });
    return response.data;
  },

  updateSystemUIConfig: async (identifier: string, uiConfig: Record<string, any>): Promise<System> => {
    const response = await api.put(`/systems/${identifier}/ui-config/`, { ui_config: uiConfig });
    return response.data;
  },

  resetSystemConfig: async (identifier: string, configType: 'config' | 'ui_config' = 'config'): Promise<System> => {
    const response = await api.post(`/systems/${identifier}/config/reset/`, { config_type: configType });
    return response.data;
  },

  getSystemConfigHistory: async (identifier: string, configType?: 'config' | 'ui_config'): Promise<ConfigHistory[]> => {
    const params = configType ? { config_type: configType } : {};
    const response = await api.get(`/systems/${identifier}/config/history/`, { params });
    return response.data;
  },

  // Update API
  checkUpdate: async (updateData: { identifier: string; current_version: string }): Promise<{ update_available: boolean; current_version: string; latest_version?: string }> => {
    const response = await api.post('/update/', updateData);
    return response.data;
  },

  // User API
  getUsers: async (): Promise<SystemUser[]> => {
    const response = await api.get('/users/list/');
    return response.data;
  },
  createUser: async (userData: { identifier: string; system_identifier: string }): Promise<SystemUser> => {
    const response = await api.post('/users/', userData);
    return response.data;
  },
  deleteUser: async (userId: number): Promise<void> => {
    await api.delete(`/users/${userId}/`);
  },

  // ============== Auth Manager API ==============

  // Sync from Platform
  syncFromPlatform: async (baseUrl?: string, token?: string): Promise<SyncResponse> => {
    const response = await api.post('/auth/sync/', { base_url: baseUrl, token });
    return response.data;
  },

  // Site Tokens
  getSiteTokens: async (): Promise<SiteToken[]> => {
    const response = await api.get('/auth/tokens/');
    return response.data;
  },

  getSiteToken: async (tokenId: number): Promise<SiteToken> => {
    const response = await api.get(`/auth/tokens/${tokenId}/`);
    return response.data;
  },

  getSiteTokenDetail: async (tokenId: number): Promise<any> => {
    const response = await api.get(`/auth/tokens/${tokenId}/detail/`);
    return response.data;
  },

  revokeSiteToken: async (tokenId: number): Promise<void> => {
    await api.delete(`/auth/tokens/${tokenId}/revoke/`);
  },

  updateSiteTokenConfig: async (tokenId: number, version: number, config: Record<string, any>, setActive: boolean = false): Promise<any> => {
    const response = await api.put(`/auth/tokens/${tokenId}/config/general/`, { version, config, set_active: setActive });
    return response.data;
  },

  updateSiteTokenImpressaConfig: async (tokenId: number, version: number, impressaConfig: Record<string, any>, setActive: boolean = false): Promise<any> => {
    const response = await api.put(`/auth/tokens/${tokenId}/config/impressa/`, { version, impressa_config: impressaConfig, set_active: setActive });
    return response.data;
  },

  updateSiteTokenUIConfig: async (tokenId: number, version: number, uiConfig: Record<string, any>, setActive: boolean = false): Promise<any> => {
    const response = await api.put(`/auth/tokens/${tokenId}/config/ui/`, { version, ui_config: uiConfig, set_active: setActive });
    return response.data;
  },

  getSiteTokenConfigHistory: async (tokenId: number): Promise<any> => {
    const response = await api.get(`/auth/tokens/${tokenId}/config/history/`);
    return response.data;
  },

  createSiteTokenConfigVersion: async (tokenId: number, config: Record<string, any>, impressaConfig: Record<string, any>, uiConfig: Record<string, any>, setActive: boolean = false): Promise<any> => {
    const response = await api.post(`/auth/tokens/${tokenId}/config/history/`, { config, impressa_config: impressaConfig, ui_config: uiConfig, set_active: setActive });
    return response.data;
  },

  // Site Users (Auth Manager)
  getAuthSiteUsers: async (siteTokenId?: number): Promise<AuthSiteUser[]> => {
    const params: Record<string, any> = {};
    if (siteTokenId) params.site_token_id = siteTokenId;
    const response = await api.get('/auth/users/', { params });
    return response.data;
  },

  getSiteUser: async (userId: number): Promise<AuthSiteUser> => {
    const response = await api.get(`/auth/users/${userId}/`);
    return response.data;
  },

  getUserDetail: async (userId: number): Promise<any> => {
    const response = await api.get(`/auth/users/${userId}/detail/`);
    return response.data;
  },

  updateSiteUser: async (userId: number, data: Partial<AuthSiteUser>): Promise<AuthSiteUser> => {
    const response = await api.put(`/auth/users/${userId}/`, data);
    return response.data;
  },

  updateUserConfig: async (userId: number, version: number, config: Record<string, any>, setActive: boolean = false): Promise<any> => {
    const response = await api.put(`/auth/users/${userId}/config/general/`, { version, config, set_active: setActive });
    return response.data;
  },

  updateUserImpressaConfig: async (userId: number, version: number, impressaConfig: Record<string, any>, setActive: boolean = false): Promise<any> => {
    const response = await api.put(`/auth/users/${userId}/config/impressa/`, { version, impressa_config: impressaConfig, set_active: setActive });
    return response.data;
  },

  updateUserUIConfig: async (userId: number, version: number, uiConfig: Record<string, any>, setActive: boolean = false): Promise<any> => {
    const response = await api.put(`/auth/users/${userId}/config/ui/`, { version, ui_config: uiConfig, set_active: setActive });
    return response.data;
  },

  approveUser: async (userId: number, approved: string): Promise<AuthSiteUser> => {
    const response = await api.post(`/auth/users/${userId}/approve/`, { approved });
    return response.data;
  },

  getUserConfigHistory: async (userId: number): Promise<any> => {
    const response = await api.get(`/auth/users/${userId}/config/history/`);
    return response.data;
  },

  createUserConfigVersion: async (userId: number, config: Record<string, any>, impressaConfig: Record<string, any>, uiConfig: Record<string, any>, setActive: boolean = false): Promise<any> => {
    const response = await api.post(`/auth/users/${userId}/config/history/`, { config, impressa_config: impressaConfig, ui_config: uiConfig, set_active: setActive });
    return response.data;
  },

  // Site Machines (Auth Manager)
  getSiteMachines: async (siteTokenId?: number): Promise<SiteMachine[]> => {
    const params: Record<string, any> = {};
    if (siteTokenId) params.site_token_id = siteTokenId;
    const response = await api.get('/auth/machines/', { params });
    return response.data;
  },

  getSiteMachine: async (machineId: number): Promise<SiteMachine> => {
    const response = await api.get(`/auth/machines/${machineId}/`);
    return response.data;
  },

  getMachineDetail: async (machineId: number): Promise<any> => {
    const response = await api.get(`/auth/machines/${machineId}/detail/`);
    return response.data;
  },

  updateSiteMachine: async (machineId: number, data: Partial<SiteMachine>): Promise<SiteMachine> => {
    const response = await api.put(`/auth/machines/${machineId}/`, data);
    return response.data;
  },

  updateMachineConfig: async (machineId: number, version: number, config: Record<string, any>, setActive: boolean = false): Promise<any> => {
    const response = await api.put(`/auth/machines/${machineId}/config/general/`, { version, config, set_active: setActive });
    return response.data;
  },

  updateMachineImpressaConfig: async (machineId: number, version: number, impressaConfig: Record<string, any>, setActive: boolean = false): Promise<any> => {
    const response = await api.put(`/auth/machines/${machineId}/config/impressa/`, { version, impressa_config: impressaConfig, set_active: setActive });
    return response.data;
  },

  updateMachineUIConfig: async (machineId: number, version: number, uiConfig: Record<string, any>, setActive: boolean = false): Promise<any> => {
    const response = await api.put(`/auth/machines/${machineId}/config/ui/`, { version, ui_config: uiConfig, set_active: setActive });
    return response.data;
  },

  approveMachine: async (machineId: number, approved: string): Promise<SiteMachine> => {
    const response = await api.post(`/auth/machines/${machineId}/approve/`, { approved });
    return response.data;
  },

  getMachineConfigHistory: async (machineId: number): Promise<any> => {
    const response = await api.get(`/auth/machines/${machineId}/config/history/`);
    return response.data;
  },

  createMachineConfigVersion: async (machineId: number, config: Record<string, any>, impressaConfig: Record<string, any>, uiConfig: Record<string, any>, setActive: boolean = false): Promise<any> => {
    const response = await api.post(`/auth/machines/${machineId}/config/history/`, { config, impressa_config: impressaConfig, ui_config: uiConfig, set_active: setActive });
    return response.data;
  },

  // ============== Admin User Management API ==============

  getAdminUsers: async (): Promise<any[]> => {
    const response = await api.get('/admin-users/');
    return response.data;
  },

  createAdminUser: async (userData: {
    username: string;
    password: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    is_staff?: boolean;
    is_superuser?: boolean;
  }): Promise<any> => {
    const response = await api.post('/admin-users/create/', userData);
    return response.data;
  },

  updateAdminUser: async (userId: number, userData: {
    email?: string;
    first_name?: string;
    last_name?: string;
    password?: string;
    is_active?: boolean;
    is_staff?: boolean;
    is_superuser?: boolean;
  }): Promise<any> => {
    const response = await api.put(`/admin-users/${userId}/`, userData);
    return response.data;
  },

  deleteAdminUser: async (userId: number): Promise<void> => {
    await api.delete(`/admin-users/${userId}/delete/`);
  },

  // ============== Feedback API ==============
  getFeedbackList: async (params: FeedbackListParams = {}): Promise<FeedbackListResponse> => {
    const response = await api.get('/widget/feedback/', { params });
    return response.data;
  },

  // ============== Build Pipeline API ==============
  getWidgetTags: async (): Promise<{ tags: string[] }> => {
    const response = await api.get('/auth/builds/widget-tags/');
    return response.data;
  },

  listArtifacts: async (siteName: string): Promise<{ versions: { version: string; electron_tag: string; filename: string }[] }> => {
    const response = await api.get('/auth/builds/artifacts/', { params: { site_name: siteName } });
    return response.data;
  },

  getArtifactUrl: async (siteName: string, electronTag: string, filename: string, copyUrl = false): Promise<{ url: string }> => {
    const params: Record<string, any> = { site_name: siteName, electron_tag: electronTag, filename };
    if (copyUrl) params.copy_url = 'true';
    const response = await api.get('/auth/builds/artifact/', { params });
    return response.data;
  },

  triggerBuild: async (siteTokenId: number, tag: string): Promise<{ status: string }> => {
    const response = await api.post('/auth/builds/trigger/', { site_token_id: siteTokenId, tag });
    return response.data;
  },

  getWorkflowStatus: async (): Promise<{ active: boolean; run: any | null }> => {
    const response = await api.get('/auth/builds/workflow-status/');
    return response.data;
  },
};

export default api;
